import React, { useState } from 'react';
import AuthLayout from '@components/Layout/auth/AuthLayout';
import { signIn, setUpTOTP, resendSignUpCode } from 'aws-amplify/auth';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setLoginEmail,
    setQrSecret,
    setMfaPreferred,
    setNewPassword,
    setSession,
    setPasswordChange,
    setLoggedInUserStatus,
    setLoggedInUserRole,
    setHospitalBlocked,
    setLoggedInUserID,
} from '@store/reducers/auth/loginSlice';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    FORGOT_PASSWORD_PATH,
    HOME_PATH,
    ONBOARDING_PATH,
    RESET_PASSWORD_PATH,
    TOTP_PATH,
    USER_ROLE_PATH,
    VERIFICATION_PATH,
} from '@constants/routes';
import { HOSPITAL, NURSE, UNCONFIRMED } from '@constants/constants';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import { FiLock, FiMail, FiShield } from 'react-icons/fi';
import { FaArrowRightLong, FaPlus } from 'react-icons/fa6';
import {
    setEmail,
    setShowLink,
    setVerified,
} from '@store/reducers/auth/signupSlice';
import { FaCircleCheck } from 'react-icons/fa6';
import ErrorMessage from '@components/Error/ErrorMessage';
import LoadingSpinner from '@components/Spinner';
import { APP_NAME } from '@constants/AppConfig';
import {
    userVerification,
} from '@utils/sharedUtils';
import { userSession, processUserDataAndValidate } from '@utils/login';
import { logout } from '@store/reducers/auth/authReducer';
import { clearLocalStoragePreserveLanguage } from '@utils/utils';
import { DRIPSET_DEVICE_SRC, DRIPSET_LOGO } from '@constants/images';

const Login = () => {
    const [email, setUserEmail] = useState<string>('');
    const [password, setUserPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userVerified = useAppSelector((state) => state.signup.verified);
    const passwordChange = useAppSelector(
        (state) => state.login.passwordChange
    );
    const pushNotificationToken = useAppSelector((state) => state.user.pushNotificationToken);

    const handleBackClick = () => {
        dispatch(setLoggedInUserRole(''));
        dispatch(setLoggedInUserStatus(''));
        dispatch(setPasswordChange(false));
        dispatch(setVerified(false));
        navigate(ONBOARDING_PATH);
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUserEmail(event.target.value);
        setError(null);
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setUserPassword(event.target.value);
        setError(null);
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const getErrorMessage = (error: any): string => {
        switch (error.name) {
            case 'UserNotFoundException':
            case UNCONFIRMED:
                // return 'Invalid username or password.';
                return 'Invalid credential.';
            case 'UserNotConfirmedException':
                return 'User not Verified.';
            case 'FORCE_CHANGE_PASSWORD':
                return 'Your password needs to be changed before you can continue. Please update your password.';
            case 'PasswordResetRequiredException':
                return 'Password reset required for this user.';
            case 'TooManyRequestsException':
                return 'Too many requests. Please try again later.';
            case 'InvalidParameterException':
                return 'Invalid parameters provided.';
            default:
                return (
                    error.message || 'An unknown error occurred during login'
                );
        }
    };

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        clearLocalStoragePreserveLanguage();
        event.preventDefault();
        setIsLoading(true);

        try {
            const output = await signIn({
                username: email,
                password,
            });

            console.log('output', output);

            let role = '';
            let currentUserId = '';

            const { nextStep, isSignedIn } = output;

                switch (nextStep.signInStep) {
                    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
                        const totpSetupDetails = nextStep.totpSetupDetails;
                        const setupUri = totpSetupDetails.getSetupUri(
                            APP_NAME,
                            email
                        );
                        const qrhref = setupUri.href;

                        dispatch(setQrSecret(qrhref));
                        dispatch(setMfaPreferred(true));
                        dispatch(setLoginEmail(email));
                        navigate(TOTP_PATH);
                        setIsLoading(false);
                        return;
                    case 'DONE':
                        const totpNewSetupDetails = await setUpTOTP();
                        const newSetupUri = totpNewSetupDetails.getSetupUri(
                            APP_NAME,
                            email
                        );
                        const new_qr_href = newSetupUri.href;

                        dispatch(setQrSecret(new_qr_href));
                        dispatch(setMfaPreferred(false));
                        dispatch(setLoginEmail(email));
                        navigate(TOTP_PATH);
                        setIsLoading(false);
                        return;
                    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
                        dispatch(setMfaPreferred(true));
                        dispatch(setLoginEmail(email));
                        navigate(TOTP_PATH);
                        setIsLoading(false);
                        return;
                    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                        const res = await userSession(email, password);
                        // console.log('res', res);
                        if (
                            res.ChallengeName === 'NEW_PASSWORD_REQUIRED' &&
                            res.Session
                        ) {
                            dispatch(setSession(res.Session));
                            dispatch(setLoggedInUserID(currentUserId));
                            dispatch(setNewPassword(true));
                            dispatch(setLoginEmail(email));
                            navigate(RESET_PASSWORD_PATH);
                            setIsLoading(false);
                            return;
                        }
                        return;
                    case 'CONFIRM_SIGN_UP':
                        await resendSignUpCode({
                            username: email,
                        });
                        dispatch(setEmail(email));
                        dispatch(setShowLink(false));
                        navigate(VERIFICATION_PATH);
                        setIsLoading(false);
                        return;
                }

                if (!isSignedIn) {
                    setIsLoading(false);
                    return;
                }

                role = await processUserDataAndValidate(
                    email,
                    pushNotificationToken || '',
                    dispatch,
                    navigate,
                    HOME_PATH,
                    setLoggedInUserRole,
                    setLoggedInUserStatus,
                    setHospitalBlocked
                );

                dispatch(setLoginEmail(email));
                dispatch(setLoggedInUserRole(role));
        } catch (error: any) {
            setError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    const forgetPassword = async () => {
        try {
            // if (email != '') {
            //     setIsLoading(true);
            //     await userVerification(email, false);
            //     dispatch(setLoginEmail(email));
            //     setIsLoading(false);
            // }
            navigate(FORGOT_PASSWORD_PATH);
        } catch (error: any) {
            setError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout
            onBackClick={handleBackClick}
            className="bg-[#edf4ff]"
            hideBrandHeader
            backButtonClassName="z-30 !text-primary left-7 md:left-10 md:top-4"
        >
            <main className="relative flex-1 px-2 pb-2 pt-0 sm:px-4">
                <section className="dripset-login-shell relative overflow-hidden rounded-xl bg-gradient-to-br from-white via-[#f8fbff] to-[#edf5ff]">
                    <div className="dripset-login-bg absolute inset-0" />
                    <div className="dripset-login-wave absolute bottom-0 left-0 h-40 w-3/5 opacity-60" />
                    <div className="dripset-login-orbit-main absolute left-[8%] top-[14%] hidden h-[470px] w-[470px] rounded-full border border-white/60 bg-[#dbe9ff]/55 lg:block" />
                    <div className="absolute left-[7%] top-[11%] hidden h-[560px] w-[560px] rounded-full border border-white/45 lg:block" />
                    <div className="absolute left-[4%] top-[26%] hidden h-7 w-7 rounded-full border-[7px] border-[#8bb7ff] opacity-90 lg:block" />
                    <div className="absolute left-[40%] top-[25%] hidden h-4 w-4 rounded-full border-4 border-[#8bb7ff] opacity-80 lg:block" />
                    <div className="absolute bottom-[30%] left-[42%] hidden h-6 w-6 rounded-full border-[6px] border-[#8bb7ff] opacity-80 lg:block" />

                    <div className="dripset-login-grid relative z-10 grid grid-cols-1 items-center gap-6 px-6 py-10 lg:grid-cols-[1fr_1.05fr] lg:px-10 lg:py-6 xl:px-16">
                        <div className="relative hidden min-h-[600px] lg:block">
                            <div className="dripset-tube absolute left-[23%] top-[-150px] h-[240px] w-28 rounded-b-[48px] border-x-[18px] border-b-[18px] border-white/70 bg-white/25" />
                            <div className="dripset-plus-bubble absolute left-[18%] top-[7%] flex h-20 w-20 items-center justify-center rounded-full bg-white/95">
                                <FaPlus className="text-4xl text-[#267dff] drop-shadow" />
                            </div>
                            <div className="absolute left-[15%] top-[-2%] z-10 h-[610px] w-[420px] overflow-hidden">
                                <img
                                    src={DRIPSET_DEVICE_SRC}
                                    alt="DripSet device"
                                    className="dripset-device-image absolute left-1/2 top-[-100px] h-[720px] w-auto -translate-x-1/2 object-contain"
                                />
                            </div>
                            <div className="absolute bottom-4 left-[5%] flex max-w-[560px] items-center gap-5">
                                <div className="dripset-shield-bubble flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#d9e8ff]">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white">
                                        <FiShield className="text-2xl" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="mb-1 text-lg font-bold text-primary">
                                        Smart IV fluid management
                                    </h3>
                                    <p className="max-w-[370px] text-base leading-6 text-[#08235e]">
                                        Real-time monitoring, precise control,
                                        and better outcomes for every patient.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mx-auto flex w-full max-w-[620px] flex-col items-center">
                            <img
                                src={DRIPSET_LOGO}
                                alt="DripSet"
                                className="mb-4 h-20 w-auto object-contain md:h-24"
                            />
                            <div className="mb-4 text-center">
                                <h1 className="mb-1 text-3xl font-bold text-[#08235e]">
                                    Welcome back!
                                </h1>
                                <p className="text-base text-[#51648a] md:text-lg">
                                    Login to your account to continue
                                </p>
                            </div>

                            {error && (
                                <div className="mb-4 w-full max-w-[520px]">
                                    <ErrorMessage message={error} />
                                </div>
                            )}

                            {userVerified && (
                                <div className="mb-4 flex w-full max-w-[520px] items-center justify-center rounded-lg bg-light_green py-2 text-sm font-medium text-green">
                                    <FaCircleCheck className="mr-3 text-xl" />
                                    {t('auth.login.user_verified')}
                                </div>
                            )}

                            {passwordChange && (
                                <div className="mb-4 flex w-full max-w-[520px] items-center justify-center rounded-lg bg-light_green py-2 text-sm font-medium text-green">
                                    <FaCircleCheck className="mr-3 text-xl" />
                                    {t('auth.login.password_change')}
                                </div>
                            )}

                            <div className="dripset-login-card w-full max-w-[560px] rounded-2xl bg-white/95 p-6 sm:p-7">
                        <form onSubmit={handleLogin}>
                            <div className="mb-4">
                                <label
                                    htmlFor="email"
                                    className="mb-2 block text-base font-medium text-[#08235e]"
                                >
                                    {t('auth.login.email_label')}
                                </label>
                                <div className="relative flex items-center">
                                    <FiMail className="absolute left-5 text-2xl text-[#6b7c9c]" />
                                    <input
                                        type="email"
                                        className="dripset-login-input h-[52px] w-full rounded-xl border border-[#c7d5eb] bg-white px-16 text-base text-[#08235e] outline-none transition placeholder:text-[#93a2bf] focus:border-primary"
                                        id="email"
                                        name="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        placeholder={t(
                                            'auth.login.email_placeholder'
                                        )}
                                        autoFocus
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                            </div>
                            <div className="mb-2 form-password-toggle">
                                <div className="d-flex justify-content-between">
                                    <label
                                        className="mb-2 block text-base font-medium text-[#08235e]"
                                        htmlFor="password"
                                    >
                                        {t('auth.login.password_label')}
                                    </label>
                                </div>
                                <div className="relative flex items-center">
                                    <FiLock className="absolute left-5 text-2xl text-[#6b7c9c]" />
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        id="password"
                                        className="dripset-login-input h-[52px] w-full rounded-xl border border-[#c7d5eb] bg-white px-16 pr-14 text-base text-[#08235e] outline-none transition placeholder:text-[#93a2bf] focus:border-primary"
                                        name="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder={t(
                                            'auth.login.password_placeholder'
                                        )}
                                        required
                                        aria-describedby="password"
                                        autoComplete="new-password"
                                    />
                                    <span
                                        className="absolute right-5 cursor-pointer text-xl text-[#6b7c9c]"
                                        onClick={togglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <IoEyeOutline />
                                        ) : (
                                            <IoEyeOffOutline />
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="mb-5 text-right text-sm font-medium">
                                {isLoading ? (
                                    <div className="text-secondary">
                                        <LoadingSpinner
                                            size="sm"
                                            className="text-secondary"
                                        />
                                    </div>
                                ) : (
                                    <div
                                        onClick={forgetPassword}
                                        className="cursor-pointer text-sm font-medium text-primary hover:underline underline-offset-4"
                                    >
                                        {t('auth.login.forgot_password')}
                                    </div>
                                )}
                            </div>
                            <div>
                                {isLoading ? (
                                    <div className="dripset-login-button flex h-[52px] w-full items-center justify-center rounded-xl !bg-primary text-base font-semibold text-white">
                                        <div>
                                            <LoadingSpinner className="text-secondary" />
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className="dripset-login-button h-[52px] w-full rounded-xl !bg-primary text-base font-semibold text-white transition hover:!bg-[#003fb0]"
                                        type="submit"
                                    >
                                        {t('auth.login.button')}
                                    </button>
                                )}
                                <div className="mt-5 border-t border-[#e1e8f5] pt-4 text-center text-base font-normal text-[#51648a]">
                                    <div>
                                        <div>
                                            {t('auth.login.no_account')}
                                        </div>
                                        <Link
                                            to={USER_ROLE_PATH}
                                            className="mt-1 inline-flex items-center gap-3 !text-primary hover:underline underline-offset-4"
                                        >
                                            {t('auth.login.create_account')}
                                            <FaArrowRightLong className="text-base" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </form>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </AuthLayout>
    );
};

export default Login;
